
import { get, merge } from 'lodash';
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { HealthStatus } from '@/definitions/app/health-status';
import { ItemsActionName, ItemsActionNames } from '@/definitions/app/item.actions.name';
import { Case, CaseStatusEnum, VideoArchive } from '@/api';
import { autoUpdateHelper } from '@/api/common/auto-update-helper';
import { ModelAclResult } from '@/store/acl/types';
import { ObjectsType, ObjectsTypesMap } from '@/store/application/data.assets';
import { dataAssetsModule } from '@/store/application/data.assets.module';
import { configModule } from '@/store/config';
import { actionHandler, convertToMsbItem } from '@/store/data/ActionHandler';
import { actionsModule } from '@/store/data/ActionsModule';
import { MultisidebarItemTypes } from '@/store/multisidebar/types';
import { RouterModule } from '@/store/router';
import { dataSourceVideosModule } from '@/store/videos';
import { websocketModule } from '@/store/ws/websocket.module';
import { Action, NButton } from '@/uikit';
import NButtonModel from '@/uikit/buttons/NButtonModel.vue';
import { NTableColumn } from '@/uikit/table-v2';
import NTable from '@/uikit/table-v2/NTable.vue';
import { createIdTableSelectionColumn } from '@/uikit/table-v2/utils';
import NTableProgressCell from '@/uikit/table/NTableProgressCell.vue';
import NText from '@/uikit/text/NText.vue';
import { formatFileSize } from '@/common/filters';
import ActionsDropdown from '@/components/common/ActionsDropdown.vue';
import CameraScreenshot from '@/components/data-source/CameraScreenshot.vue';
import { TabNames } from '@/pages/cases/EditCasePage.vue';
import CaseAclHelper from '@/pages/cases/CaseAclHelper';
import { configAclModule } from '@/store/config/acl';

@Options({
  name: 'DataSourcesVideosTable',
  components: { NButton, NTable, NTableProgressCell },
  emits: ['select']
})
export default class DataSourcesVideosTable extends Vue {
  @Prop({ type: Array, default: () => [] })
  readonly items!: VideoArchive[];

  @Prop({ type: Array, default: () => [] })
  readonly selectedItems!: number[];

  @Prop({ type: Object, required: true })
  readonly modelAcl!: ModelAclResult;

  @Prop({ type: Object, required: true })
  readonly item!: Case;

  createIdTableSelectionColumn = createIdTableSelectionColumn;
  caseAclHelper = new CaseAclHelper();

  get availableObjectsMap() {
    return dataAssetsModule.availableObjectsMap;
  }

  get columns(): NTableColumn<VideoArchive>[] {
    const baseColumns: NTableColumn<VideoArchive>[] = [
      {
        width: 'max-content',
        head: this.$t('ds.id'),
        body: ({ model }) => model.id
      },
      {
        width: 120,
        head: this.$t('ds.image'),
        body: ({ model }) => {
          const props = { width: 100, displayWidth: '100px', modelValue: model.screenshot, hasPlay: false, showErrorState: model.file_size === 0 };
          return <CameraScreenshot {...props} />;
        }
      },
      {
        width: 'minmax(160px, 1fr)',
        head: this.$t('ds.name'),
        body: ({ model }) => {
          const nameProps = {
            modelValue: model.name,
            class: 'datasource-videos-table__description-name',
            onClick: this.handleNameClick.bind(this, model)
          };
          return (
            <div class="datasource-videos-table__description">
              <NText {...nameProps} />
              <NText>{formatFileSize(model.file_size)}</NText>
            </div>
          );
        }
      }
    ];

    const progressColumn: NTableColumn<VideoArchive> = {
      width: 180,
      head: this.$t('ds.status'),
      body: ({ model }) => <NTableProgressCell {...this.statusPropsHandler(model)}></NTableProgressCell>
    };

    const caseClusterStats: NTableColumn<VideoArchive> = {
      width: 120,
      head: this.$t('ds.face_clusters', 'f'),
      body: ({ model }) => {
        const props = {
          modelValue: model.face_case_cluster_count ?? 0,
          'onUpdate:modelValue': this.navigateToCaseClusters.bind(this, model, ObjectsTypesMap.Faces),
          type: 'link'
        };
        return <NButtonModel {...props} />;
      }
    };

    const faceStats: NTableColumn<VideoArchive> = {
      width: 80,
      head: this.$t('ds.faces', 'f'),
      body: ({ model }) => {
        const props = {
          modelValue: model.face_count ?? 0,
          'onUpdate:modelValue': this.navigateToEvents.bind(this, model, ObjectsTypesMap.Faces),
          type: 'link'
        };

        return <NButtonModel {...props} />;
      }
    };

    const bodyStats: NTableColumn<VideoArchive> = {
      width: 90,
      head: this.$t('ds.bodies', 'f'),
      body: ({ model }) => {
        const props = {
          modelValue: model.body_count ?? 0,
          'onUpdate:modelValue': this.navigateToEvents.bind(this, model, ObjectsTypesMap.Bodies),
          type: 'link'
        };

        return <NButtonModel {...props} />;
      }
    };

    const carsStats: NTableColumn<VideoArchive> = {
      width: 80,
      head: this.$t('ds.cars', 'f'),
      body: ({ model }) => {
        const props = {
          modelValue: model.car_count ?? 0,
          'onUpdate:modelValue': this.navigateToEvents.bind(this, model, ObjectsTypesMap.Cars),
          type: 'link'
        };

        return <NButtonModel {...props} />;
      }
    };

    const actionsColumn: NTableColumn<VideoArchive> = {
      width: 80,
      head: '',
      body: ({ model }) => {
        const dropdownProps = {
          actions: this.getActions(model),
          placement: 'left-start',
          onAction: (action: ItemsActionName) => this.actionHandler(action, model)
        };
        return (
          <div class="datasource-videos-table__actions-cell">
            <ActionsDropdown class="datasource-videos-table__actions" {...dropdownProps} />
          </div>
        );
      }
    };

    const statColumns = [];
    if (configModule.features.cases_enabled) {
      statColumns.push(caseClusterStats);
    }
    if (configModule.features.events_enabled) {
      if (this.availableObjectsMap.face) {
        statColumns.push(faceStats);
      }

      if (this.availableObjectsMap.body) {
        statColumns.push(bodyStats);
      }

      if (this.availableObjectsMap.car) {
        statColumns.push(carsStats);
      }
    }

    return [...baseColumns, progressColumn, ...statColumns, actionsColumn];
  }

  get websocketModule() {
    return websocketModule;
  }

  created() {
    if (configAclModule.isCIBR()) {
      this.caseAclHelper.getCaseAclByCase(this.item);
    }
  }

  @Watch('websocketModule.videoUpdated')
  updateVideoArchive(updatedItem: any) {
    const item = this.items.find((v) => v.id === updatedItem.video_archive_id) as VideoArchive;
    const finished = updatedItem?.health_status?.status === HealthStatus.Completed;
    if (item) {
      const localItem = { ...updatedItem, finished };
      const remoteItem = { id: item.id, health_status: item.health_status, finished };
      merge(item, localItem);
      autoUpdateHelper.updateHandler('/videos/', item);
    }
  }

  statusPropsHandler(item: VideoArchive) {
    let result: any = dataSourceVideosModule.getStatusInfo(get(item, 'health_status.status'), item.progress, item.__upload_progress, item.finished);
    if (result.i18n_label) result.label = this.$t(result.i18n_label, 'f');
    result.error = item.error;
    return result;
  }

  handleNameClick(model: VideoArchive) {
    actionHandler.run(ItemsActionNames.ShowItem, { type: MultisidebarItemTypes.Videos, rawItem: model });
  }

  dispatchSelect(selected: VideoArchive[]) {
    this.$emit('select', selected);
  }

  async actionHandler(actionName: ItemsActionName, item: VideoArchive) {
    const requiredSaveActionItems: ItemsActionName[] = [
      ItemsActionNames.CameraResetAll,
      ItemsActionNames.CameraResetFull,
      ItemsActionNames.CameraResetAdvanced,
      ItemsActionNames.CameraResetMap,
      ItemsActionNames.CameraResetDetectors,
      ItemsActionNames.CameraResetZone
    ];

    const payload = await convertToMsbItem({ type: MultisidebarItemTypes.Videos, rawItem: item });
    const executeSaveAction = requiredSaveActionItems.includes(actionName);
    await actionHandler.run(actionName, payload);
    if (executeSaveAction) await actionHandler.run(ItemsActionNames.Save, payload);
  }

  getActions(item: VideoArchive): Action[] {
    const options = {
      hasDeleteFile: !!item.file_size,
      hasDelete: true,
      hasVideoReset: true
    };

    this.caseAclHelper.updateActionsOptions(options, item);
    return actionsModule.getItemActions(this.modelAcl, options).map(actionsModule.computeActionByName);
  }

  navigateToEvents(video: VideoArchive, objectsType: ObjectsType) {
    RouterModule.navigateToEvents(objectsType, { video_archive: video.id });
  }

  navigateToCaseClusters(video: VideoArchive, objectsType: ObjectsType) {
    this.$emit('updateSubPage', { tabName: TabNames.Clusters, caseClusterFilters: { video_archive_in: [video.id] } });
  }
}
